import React from 'react';
import { useUserConfig, useUserConfigDispatch } from '@/context/UserConfigContext';
import { useOptionalFieldValidation } from '@/components/validation';
import { ExpenseConfigForm } from '@/components/config_forms/expense/ExpenseConfigForm';
import { getDefaultExpenseCategories } from '@/constants/expenses';
import type { ExpenseConfig } from '@/types/ExpenseConfig';
import type { ExpenseCategory } from '@/types/ExpenseCategory';
import { cn } from '@/lib/utils';

export const UserExpenseConfig: React.FC = () => {
  const { state } = useUserConfig();
  const { updateBillingConfig } = useUserConfigDispatch();

  const config = state.expenseConfig || {};
  const categories = config.expenseCategories ?? getDefaultExpenseCategories();

  const onConfigChange = (updates: Partial<ExpenseConfig>) => {
    updateBillingConfig({ expenseConfig: { ...config, ...updates } });
  };

  const handleCategoriesChange = (updated: ExpenseCategory[]) => {
    updateBillingConfig({
      expenseConfig: {
        ...config,
        expenseCategories: updated,
      },
    });
  };

  // Field validations via ValidationProvider schemas
  const mileageRate = useOptionalFieldValidation('expenseMileageRate');
  const receiptReq = useOptionalFieldValidation('expenseReceiptRequiredAboveAmount');
  const markupPct = useOptionalFieldValidation('defaultExpenseMarkupPercentage');
  const markupFixed = useOptionalFieldValidation('defaultExpenseMarkupFixedAmount');

  const buildInputProps = (
    fieldKey: string,
    value: number | null,
    onChange: (value: number | null) => void,
    validation: ReturnType<typeof useOptionalFieldValidation>,
  ): Record<string, unknown> => ({
    'data-validation-key': fieldKey,
    value: value ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value ? parseFloat(e.target.value) : null;
      onChange(v);
    },
    onBlur: validation.onBlur,
    className: cn(validation.hasError && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive"),
  });

  return (
    <ExpenseConfigForm
      title="Default Expense Policies"
      description="Set your standard rules for expense reimbursement."
      expenseCategories={categories}
      onCategoriesChange={handleCategoriesChange}
      config={config}
      onConfigChange={onConfigChange}
      mileageRateProps={buildInputProps('expenseMileageRate', config.expenseMileageRate ?? null, (v) => onConfigChange({ expenseMileageRate: v }), mileageRate)}
      mileageRateError={mileageRate.errorElement}
      receiptRequirementProps={buildInputProps('expenseReceiptRequiredAboveAmount', config.expenseReceiptRequiredAboveAmount ?? null, (v) => onConfigChange({ expenseReceiptRequiredAboveAmount: v }), receiptReq)}
      receiptRequirementError={receiptReq.errorElement}
      markupPercentageProps={buildInputProps('defaultExpenseMarkupPercentage', config.defaultExpenseMarkupPercentage ?? null, (v) => onConfigChange({ defaultExpenseMarkupPercentage: v }), markupPct)}
      markupPercentageError={markupPct.errorElement}
      markupFixedAmountProps={buildInputProps('defaultExpenseMarkupFixedAmount', config.defaultExpenseMarkupFixedAmount ?? null, (v) => onConfigChange({ defaultExpenseMarkupFixedAmount: v }), markupFixed)}
      markupFixedAmountError={markupFixed.errorElement}
      context="user"
    />
  );
};
